import { HistoryModal } from "@/components/history/HistoryModal";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDate } from "@/utils/formatUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Descriptions, Divider, Flex, Popconfirm, Skeleton, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeleteAgentCompletedEducation,
  useGetAgentCompletedEducationDetail,
  useGetAgentCompletedEducations,
} from "../../../../common/api/queries";
import { PageSizes } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentEducationType } from "../../enums";
import { AgentCompletedEducation } from "../../types";
import { AgentCompletedEducationDetailModalForm } from "../forms/AgentCompletedEducationDetailModalForm";
import { AgentCompletedEducationModalForm } from "../forms/AgentCompletedEducationModalForm";

type ImportantDatesProps = {
  agentId: UUID;
  showActions?: boolean;
};

const SummaryInfo = ({ agentId, showActions }: ImportantDatesProps) => {
  const { t } = useTranslation();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const { data, isFetching } = useGetAgentCompletedEducationDetail(agentId);

  return (
    <div style={{ maxWidth: 360 }}>
      <Flex className="margin-bottom-tiny" justify="space-between">
        <div>
          <b style={{ marginRight: 4 }}>{t("agent.education.titles.summaryInfo")}</b>
          <Tooltip title={t("agent.titles.history")}>
            <span>
              <Button
                size="small"
                icon={<AntIcon type="history" />}
                type="text"
                onClick={() => setIsHistoryModalOpen(true)}
              />
            </span>
          </Tooltip>
        </div>

        {data && showActions && (
          <Button size="small" type="primary" onClick={() => setIsOpenModal(true)} icon={<AntIcon type="edit" />}>
            {t("common.edit")}
          </Button>
        )}
      </Flex>

      {isFetching ? (
        <Skeleton active />
      ) : (
        <Descriptions
          size="small"
          column={1}
          bordered
          items={[
            {
              key: 1,
              label: t(`agent.education.enums.agentEducationType.${AgentEducationType.INTRODUCTION_WEBINAR}`),
              children: data?.introductionWebinarDate ? (
                formatLocaleDate(data.introductionWebinarDate)
              ) : (
                <span className="sub-header-info">{t("common.noData")}</span>
              ),
            },
            {
              key: 2,
              label: t(`agent.education.enums.agentEducationType.${AgentEducationType.FINANCIAL_ACADEMY}`),
              children: data?.financialAcademyDate ? (
                formatLocaleDate(data.financialAcademyDate)
              ) : (
                <span className="sub-header-info">{t("common.noData")}</span>
              ),
            },
            {
              key: 3,
              label: t(`agent.education.enums.agentEducationType.${AgentEducationType.PERSONAL_ACADEMY}`),
              children: data?.personalAcademyDate ? (
                formatLocaleDate(data.personalAcademyDate)
              ) : (
                <span className="sub-header-info">{t("common.noData")}</span>
              ),
            },
          ]}
        />
      )}

      {data && (
        <AgentCompletedEducationDetailModalForm
          isOpen={isOpenModal}
          onCancel={() => setIsOpenModal(false)}
          agentId={agentId}
          data={data}
        />
      )}

      {data && (
        <HistoryModal
          item={data}
          type="agent-completed-education-detail"
          isOpen={isHistoryModalOpen}
          translationRootPath="agent.completedEducation.attrs"
          onCancel={() => setIsHistoryModalOpen(false)}
        />
      )}
    </div>
  );
};

type Props = {
  agentId: string;
  showActions?: boolean;
};

export const AgentCompletedEducationTable = ({ agentId, showActions }: Props) => {
  const { t } = useTranslation();

  const [completedEducation, setCompletedEducation] = useState<AgentCompletedEducation>();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);

  const { data, isFetching } = useGetAgentCompletedEducations({
    id: agentId,
    object: { pageSize: PageSizes.LARGE, pageIndex },
  });

  const deleteAgentCompletedEducation = useDeleteAgentCompletedEducation();

  const handleCancel = () => {
    setCompletedEducation(undefined);
    setIsOpenModal(false);
  };

  const handleChangeAgentCompletedEducation = (education: AgentCompletedEducation) => {
    setCompletedEducation(education);
    setIsOpenModal(true);
  };

  const getColumns = () => {
    const columns: ColumnsType<AgentCompletedEducation> = [
      {
        key: "completedDate",
        title: t("agent.completedEducation.attrs.completedDate"),
        ellipsis: { showTitle: false },
        width: 140,
        render: (_, record) => <Ellipsis>{formatLocaleDate(record.completedDate)}</Ellipsis>,
      },
      {
        key: "code",
        title: t("agent.education.attrs.code"),
        ellipsis: { showTitle: false },
        width: 150,
        render: (_, record) => (record.education ? <Ellipsis>{record.education.code}</Ellipsis> : undefined),
      },
      {
        key: "name",
        title: t("agent.education.attrs.name"),
        ellipsis: { showTitle: false },
        width: 250,
        render: (_, record) => (record.education ? <Ellipsis>{record.education.name}</Ellipsis> : undefined),
      },
      {
        key: "type",
        title: t("agent.education.enums.agentEducationType._label"),
        ellipsis: { showTitle: false },
        width: 180,
        render: (_, record) =>
          record.education?.type ? (
            <Ellipsis>{t(`agent.education.enums.agentEducationType.${record.education?.type}`)}</Ellipsis>
          ) : undefined,
      },
    ];

    if (showActions) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon
              icon="edit"
              color="blue"
              text={t("common.edit")}
              onClick={() => handleChangeAgentCompletedEducation(record)}
            />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("agent.education.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => deleteAgentCompletedEducation.mutate({ id1: agentId, id2: record.id })}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });
    }

    return columns;
  };

  return (
    <div>
      <Flex className="margin-bottom-medium margin-top-tiny" justify="start">
        <SummaryInfo agentId={agentId} showActions={showActions} />
      </Flex>

      <Table<AgentCompletedEducation>
        {...tableStandardProps()}
        loading={isFetching}
        columns={getColumns()}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          placement: ["bottomEnd"],
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => setPageIndex(pageIndex - 1),
        }}
      />

      {showActions ? (
        <>
          <div className={data?.pageData?.length ? "table-footer-margin" : "margin-top-small"}>
            <Button size="small" type="primary" onClick={() => setIsOpenModal(true)} icon={<AntIcon type="plus" />}>
              {t("common.add")}
            </Button>
          </div>

          <AgentCompletedEducationModalForm
            isOpen={isOpenModal}
            agentId={agentId}
            education={completedEducation}
            onCancel={handleCancel}
            isFirstEducation={!data?.pageData.length}
          />
        </>
      ) : undefined}
    </div>
  );
};
